/**
 * 登录 —— passkey 为主,注册码换新 passkey,应急后门兜底。
 *
 * 三条路的优先级就是这个顺序, UI 也照这个顺序排:
 *   ① **用 passkey 登录**(默认, 一个大按钮): Touch ID 一按, 不用输任何东西。
 *   ② **用注册码注册**(第一次用这台设备): 码要在私有网里 curl 才拿得到。
 *   ③ **应急后门令牌**(折叠在最下面): 只在 passkey 失效时用, 且【只能从私有网口进】,
 *      在 console.pgv.sh.cn 上填它必然 401 —— 所以这里写清楚, 免得人反复试。
 *
 * 令牌**验过才存**:存了一把没验的令牌,人会以为自己登进来了,然后每页各报各的错。
 */
import { useState, useEffect, FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { passkeySupported } from '@/lib/webauthn';

function FloatingParticle({ delay, duration, size, startX, startY }: {
  delay: number; duration: number; size: number; startX: number; startY: number;
}) {
  return (
    <div
      className="absolute rounded-full bg-accent-500/10"
      style={{
        width: size, height: size, left: `${startX}%`, top: `${startY}%`,
        animation: `float-up ${duration}s ${delay}s infinite ease-in-out`, opacity: 0,
      }}
    />
  );
}

function GridLine() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
          maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 70%)',
          WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, transparent 70%)',
        }}
      />
    </div>
  );
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string })?.from || '/console';
  const { user, initialized, signIn, signInWithPasskey, registerPasskey } = useAuth();

  const [token, setToken] = useState('');
  const [code, setCode] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [panel, setPanel] = useState<'none' | 'register' | 'token'>('none');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const [time, setTime] = useState('');
  const canPasskey = passkeySupported();

  // 已经有一把有效令牌就直接进去(useAuth 已经替我们验过了)
  useEffect(() => { if (initialized && user) navigate(from, { replace: true }); }, [initialized, user, navigate, from]);

  useEffect(() => {
    const update = () => setTime(new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, []);

  async function handlePasskey() {
    setError('');
    setLoading(true);
    const r = await signInWithPasskey();
    setLoading(false);
    if (!r.ok) { setError(r.error); return; }
    navigate(from, { replace: true });
  }

  async function handleRegister(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!code.trim()) { setError('请填入注册码'); return; }
    setLoading(true);
    const r = await registerPasskey(code, deviceName);
    setLoading(false);
    if (!r.ok) { setError(r.error); return; }
    navigate(from, { replace: true });
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!token.trim()) { setError('请填入 hub 令牌'); return; }
    setLoading(true);
    const r = await signIn(token);
    setLoading(false);
    if (!r.ok) { setError(r.error); return; }
    navigate(from, { replace: true });
  }

  const particles = Array.from({ length: 8 }, (_, i) => ({
    delay: i * 1.2, duration: 6 + i * 0.8,
    size: 2 + ((i * 7) % 3), startX: 10 + ((i * 37) % 80), startY: 20 + ((i * 23) % 60),
  }));

  if (!initialized) {
    return (
      <div className="min-h-screen bg-background-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="relative w-10 h-10 flex items-center justify-center">
            <div className="absolute inset-0 rounded-lg bg-accent-500/20 animate-pulse" />
            <i className="ri-loader-4-line text-2xl text-accent-400 animate-spin relative z-10" />
          </div>
          <p className="text-xs text-foreground-500 font-mono">CHECKING TOKEN...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-950 relative flex items-center justify-center overflow-hidden font-sans">
      <GridLine />
      {particles.map((p, i) => (<FloatingParticle key={i} {...p} />))}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-accent-500/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-primary-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="absolute top-0 left-0 right-0 h-10 flex items-center justify-between px-4 sm:px-6 text-[11px] font-mono text-foreground-300/50 bg-background-950/50 backdrop-blur-sm z-10">
        <div className="flex items-center gap-4">
          <span>0XMAN FLEET COMMAND</span>
          <span className="w-px h-4 bg-foreground-300/20 hidden sm:block" />
          <span className="hidden sm:inline">SYS: {time}</span>
        </div>
        <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-accent-500" />AUTH-STANDBY</span>
      </div>

      <div className="relative z-10 w-full max-w-5xl mx-auto px-5 sm:px-6 py-16 flex flex-col lg:flex-row items-center gap-10 lg:gap-20">
        <div className="flex-1 text-center lg:text-left">
          <div className="inline-flex items-center gap-3 mb-5">
            <div className="relative w-10 h-10 flex items-center justify-center">
              <div className="absolute inset-0 rounded-lg bg-accent-500/20 animate-pulse" />
              <i className="ri-robot-line text-2xl text-accent-400 relative z-10" />
            </div>
            <div>
              <span className="font-heading text-2xl font-bold text-foreground-100 tracking-tight">0xman</span>
              <span className="ml-2 text-[10px] font-mono text-accent-400 bg-accent-500/10 px-1.5 py-0.5 rounded tracking-wider">HUB</span>
            </div>
          </div>
          <h1 className="text-3xl lg:text-4xl font-heading font-bold text-foreground-100 leading-tight mb-3">
            异构终端舰队<br />
            <span className="text-accent-400">智能指挥平台</span>
          </h1>
          <p className="text-sm text-foreground-400 max-w-md leading-relaxed mx-auto lg:mx-0">
            统一管理机器狗、AI 眼镜、无人机、无人车等异构终端。
            大脑与感知留在设备上 —— 云端断了,设备照常自己干活。
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center lg:justify-start">
            {[
              { label: '设备身份', icon: 'ri-shield-keyhole-line' },
              { label: '任务台账', icon: 'ri-timer-flash-line' },
              { label: '私有组网', icon: 'ri-node-tree' },
              { label: '边缘自治', icon: 'ri-cpu-line' },
            ].map((item) => (
              <span key={item.label} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-800/60 text-foreground-300 text-xs border border-background-700/50">
                <i className={`${item.icon} text-[10px] text-accent-500`} />{item.label}
              </span>
            ))}
          </div>
        </div>

        <div className="w-full max-w-sm">
          <div className="bg-background-900/80 backdrop-blur-xl border border-background-700/50 rounded-2xl p-6 sm:p-7 shadow-2xl">
            <div className="mb-6">
              <h2 className="text-lg font-heading font-semibold text-foreground-100 mb-1">身份验证</h2>
              <p className="text-xs text-foreground-400">用这台设备上的 passkey 登录。私钥在你的设备里,服务端只有公钥。</p>
            </div>

            {/* ① 主路径:passkey。一个按钮, 不用输任何东西。 */}
            <button
              type="button" onClick={handlePasskey} disabled={loading || !canPasskey}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-accent-500 hover:bg-accent-400 text-background-950 text-sm font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {loading ? (<><i className="ri-loader-4-line text-base animate-spin" />验证中...</>)
                       : (<><i className="ri-fingerprint-line text-base" />用 passkey 登录</>)}
            </button>

            {!canPasskey && (
              <p className="mt-2 text-[10px] text-amber-400/80 leading-relaxed">
                这个浏览器不支持 passkey,或当前页面不是 https —— WebAuthn 只在安全上下文里可用。
              </p>
            )}

            {error && (
              <div className="mt-4 flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                <i className="ri-error-warning-line text-sm text-red-400 mt-px shrink-0" />
                <p className="text-xs text-red-400 leading-relaxed">{error}</p>
              </div>
            )}

            {/* ② 第一次用这台设备:注册码换 passkey。 */}
            <div className="mt-5 pt-4 border-t border-background-700/40">
              <button
                type="button"
                onClick={() => { setPanel(panel === 'register' ? 'none' : 'register'); setError(''); }}
                className="w-full flex items-center justify-between text-xs text-foreground-300 hover:text-foreground-100 transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-1.5">
                  <i className="ri-add-circle-line text-sm text-accent-500" />第一次用这台设备?
                </span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform ${panel === 'register' ? 'rotate-180' : ''}`} />
              </button>

              {panel === 'register' && (
                <form onSubmit={handleRegister} className="mt-3 space-y-3">
                  <div>
                    <label className="block text-[11px] font-medium text-foreground-400 uppercase tracking-wider mb-1.5">注册码</label>
                    {/* 占位符【不能长得像一个真码】。原来这里写的是 "7K2M-9QXB",
                        结果第一个用的人把它当成要填的内容直接点了注册 —— 在这种
                        等宽大写的场景下, 灰字示例和真实输入几乎分不出来。 */}
                    <input
                      type="text" value={code}
                      onChange={(e) => { setCode(e.target.value); setError(''); }}
                      placeholder="在这里粘贴注册码" autoComplete="off" spellCheck={false}
                      className="w-full bg-background-800/40 border border-background-700/50 focus:border-accent-500/60 text-sm font-mono tracking-widest text-foreground-100 placeholder:text-foreground-600 py-2.5 px-3 rounded-lg outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-foreground-400 uppercase tracking-wider mb-1.5">这台设备叫什么</label>
                    <input
                      type="text" value={deviceName}
                      onChange={(e) => setDeviceName(e.target.value)}
                      placeholder="给这台设备起个名字" autoComplete="off"
                      className="w-full bg-background-800/40 border border-background-700/50 focus:border-accent-500/60 text-sm text-foreground-100 placeholder:text-foreground-600 py-2.5 px-3 rounded-lg outline-none transition-colors"
                    />
                  </div>
                  <button type="submit" disabled={loading}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-background-700/60 hover:bg-background-700 text-foreground-100 text-sm font-medium transition-all disabled:opacity-50 cursor-pointer">
                    <i className="ri-fingerprint-line" />注册这台设备
                  </button>
                  {/* 注册码怎么来 —— 不写清楚的话人会在这儿卡住, 因为它【不在这个页面上】。 */}
                  <div className="text-[10px] text-foreground-500 leading-relaxed bg-background-800/40 rounded-lg p-2.5">
                    注册码要在<span className="text-foreground-300">已接入私有网</span>的机器上取,一次性、10 分钟过期:
                    <code className="block mt-1.5 text-[9px] text-accent-400/90 font-mono break-all">
                      {`curl -X POST http://100.64.0.1:35481/api/v1/auth/enroll-code -d '{"name":"你的名字"}'`}
                    </code>
                  </div>
                </form>
              )}
            </div>

            {/* ③ 应急后门。折在最下面, 并且明说它在这个域名上【用不了】—— 不写的话
                人会反复粘贴然后反复 401, 以为是自己贴错了。 */}
            <div className="mt-3 pt-3 border-t border-background-700/40">
              <button
                type="button"
                onClick={() => { setPanel(panel === 'token' ? 'none' : 'token'); setError(''); }}
                className="w-full flex items-center justify-between text-[11px] text-foreground-500 hover:text-foreground-300 transition-colors cursor-pointer"
              >
                <span className="flex items-center gap-1.5"><i className="ri-key-2-line text-xs" />应急后门令牌</span>
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${panel === 'token' ? 'rotate-180' : ''}`} />
              </button>

              {panel === 'token' && (
                <form onSubmit={handleSubmit} className="mt-3 space-y-3">
                  <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <i className="ri-information-line text-xs text-amber-400 mt-px shrink-0" />
                    <p className="text-[10px] text-amber-400/90 leading-relaxed">
                      这把令牌<span className="font-semibold">只在私有网入口认</span>。在
                      console.pgv.sh.cn 上填它一定是 401 —— 要用它得访问 hub 的私有网地址。
                    </p>
                  </div>
                  <div className={`relative rounded-lg border transition-colors ${focused ? 'border-accent-500/60 bg-background-800/80' : 'border-background-700/50 bg-background-800/40'}`}>
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                      <i className={`ri-key-2-line text-sm ${focused ? 'text-accent-400' : 'text-foreground-500'}`} />
                    </span>
                    <input
                      type="password" value={token}
                      onChange={(e) => { setToken(e.target.value); setError(''); }}
                      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
                      placeholder="OXMAN_HUB_ADMIN_TOKEN" autoComplete="off" spellCheck={false}
                      className="w-full bg-transparent text-sm font-mono text-foreground-100 placeholder:text-foreground-600 py-2.5 pl-9 pr-3 rounded-lg outline-none"
                    />
                  </div>
                  <button type="submit" disabled={loading}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-background-700/60 hover:bg-background-700 text-foreground-100 text-sm font-medium transition-all disabled:opacity-50 cursor-pointer">
                    <i className="ri-shield-user-line" />用令牌进入
                  </button>
                </form>
              )}
            </div>

            <div className="mt-5 pt-4 border-t border-background-700/40 text-[10px] text-foreground-500 leading-relaxed">
              hub 是 fail-closed 的:没有有效身份时,除探活外全部拒绝 —— 绝不无鉴权放行控制接口。
            </div>
          </div>
          <p className="mt-3 text-center text-[11px] text-foreground-500/60">受控访问系统</p>
        </div>
      </div>

      <style>{`
        @keyframes float-up {
          0%   { transform: translateY(0) translateX(0); opacity: 0; }
          20%  { opacity: 0.6; }
          80%  { opacity: 0.3; }
          100% { transform: translateY(-120px) translateX(30px); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
